import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Input,
  Select,
  SelectItem,
  Textarea,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
} from "@heroui/react";
import { API_ENDPOINTS } from "../config/api";

interface Config {
  _id: string;
  name: string;
  spreadsheetUrl: string;
  spreadsheetId: string;
  isActive: boolean;
  initialPipelineStage?: string;
  initialProcess?: string;
  initialMessage?: string;
  worksheetName?: string;
  lastRun?: string;
  createdAt: string;
  updatedAt: string;
}

interface Pipeline {
  name: string;
  color: string;
}

interface Prompt {
  _id: string;
  name: string;
  description: string;
  type: string;
}

interface Worksheet {
  id: number;
  name: string;
  index: number;
}

interface SpreadsheetConfigProps {
  onLogout: () => void;
}

export default function SpreadsheetConfig({ onLogout }: SpreadsheetConfigProps) {
  const [configs, setConfigs] = useState<Config[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [pipelinesLoading, setPipelinesLoading] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [worksheetsLoading, setWorksheetsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [spreadsheetUrl, setSpreadsheetUrl] = useState("");
  const [worksheetName, setWorksheetName] = useState("");
  const [initialPipelineStage, setInitialPipelineStage] = useState("");
  const [initialProcess, setInitialProcess] = useState("");
  const [initialMessage, setInitialMessage] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingConfig, setViewingConfig] = useState<Config | null>(null);

  const token = localStorage.getItem("token");

  // Fetch all configs, pipelines, and prompts on load
  useEffect(() => {
    fetchConfigs();
    fetchPipelines();
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.PROMPTS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPrompts(data.prompts || []);
      } else {
        console.error("Failed to fetch prompts");
        setPrompts([]);
      }
    } catch (err) {
      console.error("Error fetching prompts:", err);
      setPrompts([]);
    } finally {
      setPromptsLoading(false);
    }
  };

  const fetchWorksheets = async () => {
    if (!spreadsheetUrl.trim()) {
      setFormError("Please enter a spreadsheet URL first");
      return;
    }

    setWorksheetsLoading(true);
    setFormError("");
    
    try {
      const response = await fetch(API_ENDPOINTS.SPREADSHEET_WORKSHEETS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          spreadsheetUrl,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setWorksheets(data.worksheets || []);
        
        // Auto-select the first worksheet if none is selected
        if (data.worksheets?.length > 0 && !worksheetName) {
          setWorksheetName(data.worksheets[0].name);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch worksheets");
      }
    } catch (err: any) {
      console.error("Error fetching worksheets:", err);
      setFormError(err.message || "Failed to fetch worksheets");
      setWorksheets([]);
    } finally {
      setWorksheetsLoading(false);
    }
  };

  const fetchPipelines = async () => {
    setPipelinesLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.PIPELINES, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPipelines(data.pipelines || []);
      } else {
        console.error("Failed to fetch pipelines");
        // Set default pipeline if fetch fails
        setPipelines([{ name: "New Lead", color: "#3b82f6" }]);
      }
    } catch (err) {
      console.error("Error fetching pipelines:", err);
      // Set default pipeline if fetch fails
      setPipelines([{ name: "New Lead", color: "#3b82f6" }]);
    } finally {
      setPipelinesLoading(false);
    }
  };

  const fetchConfigs = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(API_ENDPOINTS.SPREADSHEET, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch configurations");
      }

      const data = await response.json();
      setConfigs(data.configs || []);
    } catch (err) {
      console.error("Error fetching configs:", err);
      setError("Failed to load configurations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError("");

    // Validation
    if (!worksheetName.trim()) {
      setFormError("Please select a worksheet");
      setFormLoading(false);
      return;
    }

    try {
      const url = editingId
        ? API_ENDPOINTS.SPREADSHEET_BY_ID(editingId)
        : API_ENDPOINTS.SPREADSHEET;

      const method = editingId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          spreadsheetUrl,
          initialPipelineStage,
          initialProcess,
          initialMessage,
          worksheetName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save configuration");
      }

      // Reset form
      resetForm();

      // Refresh configs list
      fetchConfigs();
    } catch (err: any) {
      console.error("Error saving config:", err);
      setFormError(err.message || "Failed to save configuration");
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (config: Config) => {
    setEditingId(config._id);
    setName(config.name);
    setSpreadsheetUrl(config.spreadsheetUrl);
    setInitialPipelineStage(config.initialPipelineStage || "");
    setInitialProcess(config.initialProcess || "");
    setInitialMessage(config.initialMessage || "");
    setShowForm(true);
    
    // Fetch worksheets first, then set the selected worksheet
    if (config.spreadsheetUrl) {
      setWorksheetsLoading(true);
      setFormError("");
      
      try {
        const response = await fetch(API_ENDPOINTS.SPREADSHEET_WORKSHEETS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            spreadsheetUrl: config.spreadsheetUrl,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setWorksheets(data.worksheets || []);
          
          // Set the worksheet name after worksheets are loaded
          setWorksheetName(config.worksheetName || "");
        } else {
          const errorData = await response.json();
          console.error("Failed to fetch worksheets:", errorData.error);
          setFormError(errorData.error || "Failed to fetch worksheets");
          setWorksheets([]);
          setWorksheetName(config.worksheetName || "");
        }
      } catch (err: any) {
        console.error("Error fetching worksheets:", err);
        setFormError(err.message || "Failed to fetch worksheets");
        setWorksheets([]);
        setWorksheetName(config.worksheetName || "");
      } finally {
        setWorksheetsLoading(false);
      }
    } else {
      setWorksheetName(config.worksheetName || "");
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !window.confirm("Are you sure you want to delete this configuration?")
    ) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.SPREADSHEET_BY_ID(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete configuration");
      }

      // Refresh configs list
      fetchConfigs();
    } catch (err) {
      console.error("Error deleting config:", err);
      setError("Failed to delete configuration");
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const response = await fetch(API_ENDPOINTS.SPREADSHEET_BY_ID(id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          isActive: !currentStatus,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update configuration");
      }

      // Refresh configs list
      fetchConfigs();
    } catch (err) {
      console.error("Error updating config:", err);
      setError("Failed to update configuration");
    }
  };

  const resetForm = () => {
    setName("");
    setSpreadsheetUrl("");
    setWorksheetName("");
    setInitialPipelineStage("");
    setInitialProcess("");
    setInitialMessage("");
    setWorksheets([]);
    setEditingId(null);
    setShowForm(false);
    setFormError("");
  };

  const getPromptName = (promptId: string) => {
    const prompt = prompts.find(p => p._id === promptId);
    return prompt ? prompt.name : "Unknown Prompt";
  };

  const handleView = (config: Config) => {
    setViewingConfig(config);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingConfig(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Spreadsheet Configurations
          </h1>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>Welcome, {JSON.parse(localStorage.getItem("user") || "{}").email || "User"}</span>
            </div>
            <Button
              color="danger"
              variant="flat"
              onClick={onLogout}
              className="px-4 py-2"
            >
              Logout
            </Button>
            <Button
              color="primary"
              onClick={() => setShowForm(!showForm)}
              className="px-4 py-2"
            >
              {showForm ? "Cancel" : "Add New Configuration"}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}

        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
          >
            <Card className="w-full max-w-6xl mx-auto">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 px-8 py-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingId ? "Edit Configuration" : "New Configuration"}
                  </h2>
                  <div className="text-sm text-gray-600">
                    Configure your Google Spreadsheet scraping settings
                  </div>
                </div>
              </CardHeader>
              <Divider />
              <CardBody className="px-8 py-8">
                {formError && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-r-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium">{formError}</p>
                      </div>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Basic Configuration Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Basic Configuration
                    </h3>
                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <label
                          htmlFor="name"
                          className="text-sm font-medium text-gray-700"
                        >
                          Configuration Name
                        </label>
                        <Input
                          id="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="My Google Sheet Configuration"
                          required
                          fullWidth
                          size="lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Spreadsheet Configuration Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      Google Spreadsheet Settings
                    </h3>
                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <label
                          htmlFor="spreadsheetUrl"
                          className="text-sm font-medium text-gray-700"
                        >
                          Google Spreadsheet URL
                          <span className="ml-1 text-xs text-gray-500">
                            (Should be public and published to web)
                          </span>
                        </label>
                        <div className="flex space-x-3">
                          <Input
                            id="spreadsheetUrl"
                            value={spreadsheetUrl}
                            onChange={(e) => {
                              setSpreadsheetUrl(e.target.value);
                              // Clear worksheets when URL changes
                              setWorksheets([]);
                              setWorksheetName("");
                            }}
                            placeholder="https://docs.google.com/spreadsheets/d/..."
                            required
                            fullWidth
                            size="lg"
                          />
                          <Button
                            color="primary"
                            variant="solid"
                            onClick={fetchWorksheets}
                            disabled={!spreadsheetUrl.trim() || worksheetsLoading}
                            className="px-6 py-2 min-w-fit whitespace-nowrap h-12"
                          >
                            {worksheetsLoading ? (
                              <>
                                <Spinner size="sm" color="current" />
                                <span className="ml-2">Loading...</span>
                              </>
                            ) : (
                              "Fetch Worksheets"
                            )}
                          </Button>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-700">
                            <strong>Example:</strong> https://docs.google.com/spreadsheets/d/1ABC123_example/pubhtml
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            Make sure your spreadsheet is published to the web and publicly accessible.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="worksheetName"
                          className="text-sm font-medium text-gray-700"
                        >
                          Worksheet/Tab Name
                          <span className="ml-1 text-xs text-gray-500">
                            (Select which tab/sheet to scrape from)
                          </span>
                        </label>
                        <Select
                          id="worksheetName"
                          selectedKeys={worksheetName ? [worksheetName] : []}
                          onSelectionChange={(keys) => {
                            const selectedKey = Array.from(keys)[0] as string;
                            setWorksheetName(selectedKey || "");
                          }}
                          placeholder={
                            worksheetsLoading
                              ? "Loading worksheets..."
                              : worksheets.length === 0
                              ? "Click 'Fetch Worksheets' first"
                              : "Select worksheet"
                          }
                          isDisabled={worksheetsLoading || worksheets.length === 0}
                          fullWidth
                          size="lg"
                        >
                          {worksheets.map((worksheet) => (
                            <SelectItem key={worksheet.name}>
                              {worksheet.name}
                            </SelectItem>
                          ))}
                        </Select>
                        {worksheets.length > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-xs text-green-700">
                              ✓ Found {worksheets.length} worksheet(s). Select the one you want to scrape.
                            </p>
                          </div>
                        )}
                        {!worksheetsLoading && worksheets.length === 0 && spreadsheetUrl && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-xs text-amber-700">
                              Click "Fetch Worksheets" to load available worksheets from your spreadsheet.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Automation Settings Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Automation Settings
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label
                          htmlFor="initialPipelineStage"
                          className="text-sm font-medium text-gray-700"
                        >
                          Initial Pipeline Stage
                          <span className="ml-1 text-xs text-gray-500">
                            (Stage for new contacts from this spreadsheet)
                          </span>
                        </label>
                        <Select
                          id="initialPipelineStage"
                          selectedKeys={initialPipelineStage ? [initialPipelineStage] : []}
                          onSelectionChange={(keys) => {
                            const selectedKey = Array.from(keys)[0] as string;
                            setInitialPipelineStage(selectedKey || "");
                          }}
                          placeholder={pipelinesLoading ? "Loading pipelines..." : "Select pipeline stage"}
                          isDisabled={pipelinesLoading}
                          fullWidth
                          size="lg"
                        >
                          {pipelines.map((pipeline) => (
                            <SelectItem key={pipeline.name}>
                              {pipeline.name}
                            </SelectItem>
                          ))}
                        </Select>
                        {pipelines.length === 0 && !pipelinesLoading && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-xs text-amber-700">
                              No pipeline stages found. "New Lead" will be used as default.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="initialProcess"
                          className="text-sm font-medium text-gray-700"
                        >
                          Initial Process
                          <span className="ml-1 text-xs text-gray-500">
                            (Process for new contacts from this spreadsheet)
                          </span>
                        </label>
                        <Select
                          id="initialProcess"
                          selectedKeys={initialProcess ? [initialProcess] : []}
                          onSelectionChange={(keys) => {
                            const selectedKey = Array.from(keys)[0] as string;
                            setInitialProcess(selectedKey || "");
                          }}
                          placeholder={promptsLoading ? "Loading prompts..." : "Select prompt"}
                          isDisabled={promptsLoading}
                          fullWidth
                          size="lg"
                        >
                          {prompts.map((prompt) => (
                            <SelectItem key={prompt._id}>
                              {prompt.name}
                            </SelectItem>
                          ))}
                        </Select>
                        {prompts.length === 0 && !promptsLoading && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-xs text-amber-700">
                              No prompts found. "No Prompt" will be used as default.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 space-y-2">
                      <label
                        htmlFor="initialMessage"
                        className="text-sm font-medium text-gray-700"
                      >
                        Initial Message
                        <span className="ml-1 text-xs text-gray-500">
                          (Message to be sent to new contacts from this spreadsheet)
                        </span>
                      </label>
                      <Textarea
                        id="initialMessage"
                        value={initialMessage}
                        onChange={(e) => setInitialMessage(e.target.value)}
                        placeholder="Hello! Thanks for your interest. We'll be in touch soon!"
                        fullWidth
                        minRows={4}
                        maxRows={8}
                        size="lg"
                      />
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-600">
                          <strong>Tip:</strong> You can use placeholders like @firstName, @lastName, @email, etc. to personalize messages.
                        </p>
                      </div>
                    </div>
                  </div>
                </form>
              </CardBody>
              <Divider />
              <CardFooter className="px-8 py-6 bg-gray-50 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {editingId ? "Update your spreadsheet configuration" : "Create a new spreadsheet configuration"}
                </div>
                <div className="flex space-x-4">
                  <Button
                    color="default"
                    variant="flat"
                    onClick={resetForm}
                    disabled={formLoading}
                    size="lg"
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={formLoading}
                    size="lg"
                    className="px-8"
                  >
                    {formLoading ? (
                      <>
                        <Spinner size="sm" color="current" />
                        <span className="ml-2">Saving...</span>
                      </>
                    ) : editingId ? (
                      "Update Configuration"
                    ) : (
                      "Create Configuration"
                    )}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </motion.div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Spinner size="lg" />
          </div>
        ) : configs.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No configurations found
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first Google Spreadsheet configuration to start
              scraping data.
            </p>
            <Button
              color="primary"
              onClick={() => setShowForm(true)}
              className="px-4 py-2"
            >
              Add Configuration
            </Button>
          </div>
        ) : (
          <Card className="w-full">
            <CardBody className="px-0">
              <Table aria-label="Spreadsheet configurations">
                <TableHeader>
                  <TableColumn>NAME</TableColumn>
                  <TableColumn>WORKSHEET</TableColumn>
                  <TableColumn>STATUS</TableColumn>
                  <TableColumn>ACTIONS</TableColumn>
                </TableHeader>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow key={config._id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{config.name}</span>
                          <span className="text-xs text-gray-500 font-mono mt-1">
                            ID: {config.spreadsheetId.substring(0, 20)}...
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                          {config.worksheetName || "Sheet1"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            config.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {config.isActive ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            color="default"
                            variant="flat"
                            size="sm"
                            onClick={() => handleView(config)}
                            className="min-w-fit"
                          >
                            View
                          </Button>
                          <Button
                            color="primary"
                            variant="flat"
                            size="sm"
                            onClick={() => handleEdit(config)}
                          >
                            Edit
                          </Button>
                          <Button
                            color={config.isActive ? "warning" : "success"}
                            variant="flat"
                            size="sm"
                            onClick={() =>
                              toggleActive(config._id, config.isActive)
                            }
                          >
                            {config.isActive ? "Pause" : "Activate"}
                          </Button>
                          <Button
                            color="danger"
                            variant="flat"
                            size="sm"
                            onClick={() => handleDelete(config._id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardBody>
          </Card>
        )}

        {/* View Configuration Modal */}
        {showViewModal && viewingConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-8 py-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Configuration Details</h2>
                    <p className="text-sm text-gray-600 mt-1">View spreadsheet configuration settings</p>
                  </div>
                  <Button
                    color="default"
                    variant="flat"
                    onClick={closeViewModal}
                    className="p-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8 overflow-y-auto flex-1">
                <div className="space-y-8">
                  {/* Basic Information Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Configuration Name</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="text-gray-900">{viewingConfig.name}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Status</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            viewingConfig.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                          }`}>
                            {viewingConfig.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Spreadsheet Information Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      Spreadsheet Information
                    </h3>
                    <div className="space-y-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Spreadsheet URL</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <a 
                            href={viewingConfig.spreadsheetUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline break-all"
                          >
                            {viewingConfig.spreadsheetUrl}
                          </a>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Spreadsheet ID</label>
                          <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <span className="font-mono text-sm text-gray-900">{viewingConfig.spreadsheetId}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">Worksheet Name</label>
                          <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {viewingConfig.worksheetName || "Sheet1"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Automation Settings Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Automation Settings
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Initial Pipeline Stage</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {viewingConfig.initialPipelineStage || "New Lead"}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Initial Process</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {viewingConfig.initialProcess ? getPromptName(viewingConfig.initialProcess) : "No Prompt"}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {viewingConfig.initialMessage && (
                      <div className="mt-6">
                        <label className="text-sm font-medium text-gray-700">Initial Message</label>
                        <div className="mt-1 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-gray-900 whitespace-pre-wrap">{viewingConfig.initialMessage}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Timestamps Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Timeline Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Created</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="text-gray-900">{new Date(viewingConfig.createdAt).toLocaleString()}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Last Updated</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="text-gray-900">{new Date(viewingConfig.updatedAt).toLocaleString()}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Last Run</label>
                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="text-gray-900">
                            {viewingConfig.lastRun ? new Date(viewingConfig.lastRun).toLocaleString() : "Never"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
                <div className="text-sm text-gray-600 flex-1 min-w-0">
                  <span className="hidden sm:inline">Configuration ID: </span>
                  <span className="font-mono text-xs break-all">{viewingConfig._id}</span>
                </div>
                <div className="flex space-x-3 ml-4">
                  <Button
                    color="primary"
                    variant="flat"
                    onClick={() => {
                      closeViewModal();
                      handleEdit(viewingConfig);
                    }}
                    size="sm"
                  >
                    Edit Configuration
                  </Button>
                  <Button
                    color="default"
                    variant="solid"
                    onClick={closeViewModal}
                    size="sm"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
